import { uploadFileApi } from "@/api/services/global/file";
import { IconAttachFile } from "@/assets/icons";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { Input } from "@/components/elements/Input";
import { ACCEPT_ERROR, COMMON_FILE_TYPES_EXTENSION, COMMON_MINE_TYPES, FILE_UPLOAD_FOR, MAX_SIZE_ERROR, MIME_TYPES } from "@/constants/file";
import { MessageFileItem } from "@/features/chat/components/MessageFileItem";
import { useChat } from "@/features/chat/providers/ChatProvider";
import { background, button, divider } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { showError } from "@/utils/error";
import { checkFileListWhetherIsActualPdf, convertMbToByte, getExtensionsOfMimeTypes, TFileRules } from "@/utils/file";
import { toast } from "@/utils/toast";
import { Box, CircularProgress, Stack, styled, Typography } from "@mui/material";
import React, { KeyboardEventHandler, useEffect, useState, useRef, useCallback } from "react";
import { useDropzone } from "react-dropzone";

export const InputMsg = ({
  chatBoxRef,
  roomHeadRef,
}: {
  chatBoxRef?: React.RefObject<HTMLDivElement>;
  roomHeadRef?: React.RefObject<HTMLDivElement>;
}) => {
  const { currentRoom } = useChat();
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [showDropZone, setShowDropZone] = useState(false);

  const rules: TFileRules = {
    acceptMimeTypes: COMMON_MINE_TYPES,
    acceptFileTypesExtension: COMMON_FILE_TYPES_EXTENSION,
  };

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const inputRowRef = useRef<HTMLDivElement | null>(null);
  const uploadedFilesRef = useRef<HTMLDivElement | null>(null);
  const inputWrapperRef = useRef<HTMLDivElement | null>(null);

  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  const [maxWrapperHeight, setMaxWrapperHeight] = useState<number | undefined>(undefined);

  const recalcHeights = useCallback(() => {
    if (!chatBoxRef?.current || !roomHeadRef?.current || !inputRowRef.current) return;

    const chatBoxRect = chatBoxRef.current.getBoundingClientRect();
    const roomHeadRect = roomHeadRef.current.getBoundingClientRect();

    const available = Math.max(0, chatBoxRect.bottom - roomHeadRect.bottom);

    const style = window.getComputedStyle(inputRowRef.current);
    const paddingTop = parseFloat(style.paddingTop) || 0;
    const paddingBottom = parseFloat(style.paddingBottom) || 16;
    const gap = parseFloat(style.gap) || 8;

    const buttons = inputRowRef.current.querySelectorAll("button");
    const buttonHeight = buttons.length > 0 ? buttons[0].getBoundingClientRect().height : 40;
    const fixedElements = paddingTop + paddingBottom + buttonHeight + gap;

    let uploadedHeight = 0;
    if (uploadedFilesRef.current) {
      const r = uploadedFilesRef.current.getBoundingClientRect();
      uploadedHeight = r.height > 0 ? r.height + 8 : 0;
    }

    const textareaMax = Math.max(60, available - fixedElements - uploadedHeight);
    const wrapperH = Math.max(textareaMax + fixedElements + uploadedHeight, 60);

    setMaxHeight(textareaMax);
    setMaxWrapperHeight(wrapperH);
  }, [chatBoxRef, roomHeadRef, uploadedFiles.length]);

  useEffect(() => {
    recalcHeights();

    const ro = new ResizeObserver(() => recalcHeights());
    try {
      if (roomHeadRef?.current) ro.observe(roomHeadRef.current);
      if (chatBoxRef?.current) ro.observe(chatBoxRef.current);
      if (uploadedFilesRef.current) ro.observe(uploadedFilesRef.current);
    } catch {}

    window.addEventListener("resize", recalcHeights);
    window.addEventListener("orientationchange", recalcHeights);
    window.addEventListener("scroll", recalcHeights, true);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalcHeights);
      window.removeEventListener("orientationchange", recalcHeights);
      window.removeEventListener("scroll", recalcHeights, true);
    };
  }, [recalcHeights, roomHeadRef, chatBoxRef]);

  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;

    const timeoutId = setTimeout(() => {
      if (!inputValue || inputValue.trim() === "") {
        ta.style.height = "45px";
        ta.style.minHeight = "45px";
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, []);

  // auto-resize textarea, cap at maxHeight
  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;

    const setHeight = () => {
      // If input is empty, set height to 45px directly
      if (!inputValue || inputValue.trim() === "") {
        ta.style.height = "45px";
        ta.style.minHeight = "45px";
        ta.style.maxHeight = maxHeight ? `${maxHeight}px` : "none";
        return;
      }

      // Reset height to auto to get scrollHeight
      ta.style.height = "45px";
      const scrollHeight = ta.scrollHeight;

      const lineHeight = 24;
      const padding = 21;
      const singleLineHeight = lineHeight + padding;

      if (scrollHeight <= singleLineHeight) {
        ta.style.height = "45px";
        ta.style.minHeight = "45px";
        ta.style.maxHeight = maxHeight ? `${maxHeight}px` : "none";
        return;
      }

      let finalHeight = scrollHeight;

      if (maxHeight && finalHeight > maxHeight) {
        finalHeight = maxHeight;
      }

      ta.style.height = `${finalHeight}px`;
      ta.style.minHeight = "45px";
      ta.style.maxHeight = maxHeight ? `${maxHeight}px` : "none";

      // Auto scroll to bottom if content exceeds max height
      if (scrollHeight > finalHeight) {
        ta.scrollTop = ta.scrollHeight;
      }
    };

    setHeight();

    requestAnimationFrame(() => {
      setHeight();
      requestAnimationFrame(() => {
        setHeight();
      });
    });
  }, [inputValue, maxHeight]);

  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    const cur = ta.scrollTop;
    void ta.offsetHeight;
    if (cur > 0) ta.scrollTop = cur;
  }, [inputValue]);

  const handleSendMessage = async () => {
    try {
      setLoading(true);
      const arrFilePath: string[] = [];
      if (uploadedFiles.length > 0) {
        const extensions = getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []);

        for (const file of uploadedFiles) {
          const isValid = file.type
            ? (rules.acceptMimeTypes || []).includes(file.type)
            : extensions.includes(file.name.split(".").pop()?.toLowerCase() ?? "");

          if (!isValid) {
            toast.error(ACCEPT_ERROR(extensions));
            return;
          }
          const rs = await uploadFileApi({ file, uploadFor: FILE_UPLOAD_FOR.CHAT_FILE });
          arrFilePath.push(rs.data.filePath);
        }
      }
      await currentRoom.sendMsg(inputValue, arrFilePath);
      setInputValue("");
      setUploadedFiles([]);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFile = async (files: File[]) => {
    if (files.some((file) => file.size === 0)) {
      toast.error("0Byteのファイルはアップロードできません。");
      return;
    }
    if (
      !!rules.acceptMimeTypes &&
      files.some((file) => !rules.acceptMimeTypes?.includes(file.name.includes(".rar") ? "application/vnd.rar" : file.type))
    ) {
      const extensions = getExtensionsOfMimeTypes(rules?.acceptFileTypesExtension || []);
      toast.error(ACCEPT_ERROR(extensions));
      return;
    }

    const invalidPdf = await checkFileListWhetherIsActualPdf(files);
    if (invalidPdf) {
      toast.error("アップロードされたファイルが正しいPDF形式でないため、アップロードできません。");
      return;
    }

    const maxSize = convertMbToByte(10);
    if (files.some((f) => f.size > maxSize)) {
      toast.error(MAX_SIZE_ERROR(maxSize));
      return;
    }

    if (files.length + uploadedFiles.length > 8) {
      toast.error(`ファイルは最大8件までアップロードできます。`);
      return;
    }

    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const handlePaste = async (event: React.ClipboardEvent<HTMLInputElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const file = items[i].getAsFile();
      if (file) files.push(file);
    }
    if (files.length > 0) {
      event.preventDefault();
      if (files.length + uploadedFiles.length > 8) {
        toast.error(`ファイルは最大8件までアップロードできます。`);
        return;
      }
      setUploadedFiles((prev) => [...prev, ...files]);
    }
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = async (e) => {
    if (loading || e.nativeEvent.isComposing) return;
    if (e.key === "Enter" && e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      if (!inputValue && uploadedFiles.length === 0) return;
      await handleSendMessage();
    }
  };

  const [isInputFocused, setIsInputFocused] = useState(false);
  const onFocus = async () => {
    setIsInputFocused(true);
    await currentRoom?.readMsg();
  };
  const onBlur = () => setIsInputFocused(false);

  useEffect(() => {
    if (!isInputFocused) return;
    const getTextarea = (): HTMLTextAreaElement | null => {
      if (!inputWrapperRef.current) return null;
      return inputWrapperRef.current.querySelector("textarea");
    };

    const handleWheel = (e: WheelEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;
      const ta = getTextarea();
      if (ta && (target === ta || ta.contains(target))) {
        const { scrollTop, scrollHeight, clientHeight } = ta;
        const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
        const isDown = e.deltaY > 0;
        const isUp = e.deltaY < 0;
        const canDown = isDown && scrollTop < maxScrollTop - 1;
        const canUp = isUp && scrollTop > 1;
        if (canDown || canUp) {
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (uploadedFilesRef.current && uploadedFilesRef.current.contains(target)) {
        e.stopPropagation();
        return;
      }

      if (inputRowRef.current && inputRowRef.current.contains(target)) {
        e.stopPropagation();
        return;
      }

      e.preventDefault();
    };

    const handleTouchMove = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;
      if (uploadedFilesRef.current && uploadedFilesRef.current.contains(target)) return;
      if (inputWrapperRef.current && inputWrapperRef.current.contains(target)) return;
      if (inputRowRef.current && inputRowRef.current.contains(target)) return;
      e.preventDefault();
    };

    document.addEventListener("wheel", handleWheel, { passive: false });
    document.addEventListener("touchmove", handleTouchMove, { passive: false });

    return () => {
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, [isInputFocused]);

  const onDrop = async (acceptedFiles: File[]) => {
    setShowDropZone(false);
    try {
      if (acceptedFiles.some((file) => file.size === 0)) {
        toast.error("0Byteのファイルはアップロードできません。");
        return;
      }
      const invalidPdf = await checkFileListWhetherIsActualPdf(acceptedFiles);
      if (invalidPdf) {
        toast.error("アップロードされたファイルが正しいPDF形式でないため、アップロードできません。");
        return;
      }
      if (acceptedFiles.length + uploadedFiles.length > 8) {
        toast.error(`ファイルは最大8件までアップロードできます。`);
        return;
      }
      setUploadedFiles((prev) => [...prev, ...acceptedFiles]);
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      setShowDropZone(true);
    };
    const handleDragLeave = (e: DragEvent) => {
      if (e.relatedTarget === null) setShowDropZone(false);
    };
    const handleDragEnd = (e: DragEvent) => {
      e.preventDefault();
      setShowDropZone(false);
    };
    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("dragend", handleDragEnd);
    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("dragend", handleDragEnd);
    };
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, noClick: true });

  return (
    <>
      <InputMsgWrapper
        ref={inputRowRef}
        sx={maxWrapperHeight ? { maxHeight: `${maxWrapperHeight}px`, overflow: "auto" } : { maxHeight: "220px", overflow: "auto" }}
      >
        <Button component="label" sx={{ flexShrink: 0 }}>
          <IconAttachFile width={24} height={24} />
          <VisuallyHiddenInput
            type="file"
            onChange={(event) => event.target.files && handleUploadFile(Array.from(event.target.files))}
            accept={(rules?.acceptFileTypesExtension || []).join(",")}
            onClick={(event) => {
              event.currentTarget.value = "";
            }}
            multiple={true}
          />
        </Button>

        <Box flex={1} position="relative">
          {/* render uploaded files inline (not absolute) so they are always visible */}
          {uploadedFiles.length > 0 && (
            <Stack
              ref={uploadedFilesRef}
              direction="row"
              flexWrap="wrap"
              columnGap={4}
              rowGap={1}
              width="calc(100% - 32px)"
              maxWidth={620}
              mb={1}
              zIndex={1000}
              p={1}
              borderRadius={rounded.sm}
              border={`1px solid ${divider.middle}`}
              sx={{
                bgcolor: background.primary,
                maxHeight: "200px",
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehavior: "contain",
              }}
            >
              {uploadedFiles.map((file, index) => {
                if (file.type && MIME_TYPES.IMAGE.includes(file.type)) {
                  return (
                    <Avatar
                      key={index}
                      imageSrc={URL.createObjectURL(file)}
                      width={120}
                      height={120}
                      onRemove={() => setUploadedFiles((prev) => prev.filter((item, idx) => idx !== index))}
                    />
                  );
                }
                return (
                  <MessageFileItem key={index} file={file} onRemove={() => setUploadedFiles((prev) => prev.filter((item, idx) => idx !== index))} />
                );
              })}
            </Stack>
          )}
          <InputWrapper ref={inputWrapperRef}>
            <Input
              inputRef={inputRef}
              multiline
              autoComplete="off"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
              onBlur={onBlur}
              onPaste={handlePaste}
              fullWidth
              size="lg"
              disabled={loading}
              sx={{
                "& .MuiInputBase-root": {
                  alignItems: "flex-start",
                  minHeight: "45px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: "none !important",
                },
                "& .MuiInputAdornment-root": {
                  marginTop: "20px !important",
                  alignSelf: "flex-start",
                },
                textarea: {
                  minHeight: "45px !important",
                  maxHeight: maxHeight ? `${maxHeight}px !important` : "none",
                  overflowY: "auto !important",
                  overflowX: "hidden",
                  resize: "none",
                  padding: "10.5px 16px",
                  overscrollBehavior: "contain",
                  lineHeight: "24px",
                  boxSizing: "border-box",
                },
              }}
            />
          </InputWrapper>
        </Box>

        <Button onClick={handleSendMessage} disabled={loading || (!inputValue && uploadedFiles.length === 0)} sx={{ flexShrink: 0 }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : <Typography variant="sub16Semi">＞</Typography>}
        </Button>
      </InputMsgWrapper>

      {showDropZone && (
        <Box
          {...getRootProps()}
          position="absolute"
          zIndex={5000}
          width="100%"
          height={540}
          sx={{
            border: `1px dashed ${divider.middle}`,
            borderColor: isDragActive ? button.primary : undefined,
            backgroundColor: background.white,
            opacity: 0.9,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <input {...getInputProps()} />
          <Typography variant="body14">こちらにドラッグ＆ドロップしてください</Typography>
        </Box>
      )}
    </>
  );
};

const InputMsgWrapper = styled(Box)`
  position: relative;
  padding: 0 16px 16px;
  display: flex;
  gap: 8px;
  align-items: flex-end;
  flex-shrink: 0;
  flex-grow: 0;
  z-index: 1;
  background-color: ${background.white};
  .MuiInputBase-root {
    padding: 0;
  }
  padding-top: 5px;
`;

const InputWrapper = styled(Box)`
  flex: 1;
  min-width: 0;
  border-radius: 4px;
  overflow: hidden;
  box-shadow:
    0 -2px 4px rgba(0, 0, 0, 0.05),
    0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  width: 100%;
  > div {
    flex: 1;
    min-width: 0;
  }
`;

const VisuallyHiddenInput = styled("input")({
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
});
